import { useEffect, useState } from "react"
import { Modal, Form, Input, InputNumber, Upload, Button, Image, message } from "antd"
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons"
import type React from "react"
import { levelService, type LevelItem, type LevelFormValues } from "@/services/gamification/levelApi"

interface LevelModalProps {
  open: boolean
  initialData?: LevelItem | null
  onClose: () => void
  onSuccess: () => void
}

const LevelModal: React.FC<LevelModalProps> = ({ open, initialData, onClose, onSuccess }) => {
  const [form] = Form.useForm<LevelFormValues>()
  const [submitting, setSubmitting] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isImageDeleted, setIsImageDeleted] = useState(false)

  const isEdit = !!initialData?.id

  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          name: initialData.name,
          min_point: initialData.min_point,
          description: initialData.description || "",
        })
        setImagePreview(initialData.image_url || initialData.image_path || null)
        setImageFile(null)
        setIsImageDeleted(false)
      } else {
        form.resetFields()
        form.setFieldsValue({
          name: "",
          min_point: 0,
          description: "",
        })
        setImagePreview(null)
        setImageFile(null)
        setIsImageDeleted(false)
      }
    }
  }, [open, initialData, form])

  const handleFileChange = (file: File) => {
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setIsImageDeleted(false)
    return false // prevent auto upload
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setIsImageDeleted(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)

      const formData = new FormData()
      formData.append("name", values.name.trim())
      formData.append("min_point", String(values.min_point ?? 0))
      if (values.description !== undefined && values.description !== null) {
        formData.append("description", values.description.trim())
      }

      if (imageFile) {
        formData.append("file", imageFile)
      } else if (isEdit && isImageDeleted) {
        formData.append("image_path", "")
      }

      if (isEdit && initialData?.id) {
        const res = await levelService.update(initialData.id, formData)
        if (res.code === 200 || res.success) {
          message.success("Cập nhật cấp độ thành công")
          onSuccess()
          onClose()
        }
      } else {
        const res = await levelService.create(formData)
        if (res.code === 200 || res.success) {
          message.success("Thêm cấp độ mới thành công")
          onSuccess()
          onClose()
        }
      }
    } catch (err: any) {
      if (err?.message) {
        message.error(err.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={
        <span className="font-semibold text-base">
          {isEdit ? `Chỉnh sửa Cấp độ: ${initialData?.name}` : "Thêm Cấp độ mới"}
        </span>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText={isEdit ? "Cập nhật" : "Tạo mới"}
      cancelText="Hủy"
      destroyOnClose
      width={540}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="name"
          label="Tên cấp độ"
          rules={[
            { required: true, message: "Vui lòng nhập tên cấp độ" },
            { max: 50, message: "Tên cấp độ không vượt quá 50 ký tự" },
          ]}
        >
          <Input placeholder="Ví dụ: Tân binh, Khám phá, Bậc thầy..." />
        </Form.Item>

        <Form.Item
          name="min_point"
          label="Điểm tối thiểu yêu cầu (min_point)"
          rules={[
            { required: true, message: "Vui lòng nhập số điểm tối thiểu" },
            { type: "number", min: 0, message: "Điểm tối thiểu phải lớn hơn hoặc bằng 0" },
          ]}
        >
          <InputNumber<number>
            className="w-full"
            placeholder="0"
            min={0}
            step={50}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) =>
              value ? Number(value.replace(/\$\s?|(,*)/g, "")) : 0
            }
          />
        </Form.Item>

        <Form.Item name="description" label="Mô tả" rules={[{ max: 255, message: "Mô tả không vượt quá 255 ký tự" }]}>
          <Input.TextArea rows={3} placeholder="Nhập mô tả ngắn gọn về cấp độ này..." showCount maxLength={255} />
        </Form.Item>

        <Form.Item label="Biểu tượng / Huy hiệu cấp độ">
          {imagePreview ? (
            <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <Image
                src={imagePreview}
                alt="Preview"
                width={64}
                height={64}
                className="rounded-lg border border-gray-100 bg-white object-contain p-1"
              />
              <div className="flex flex-col gap-2">
                <Upload beforeUpload={handleFileChange} showUploadList={false} accept="image/*">
                  <Button size="small" icon={<UploadOutlined />}>
                    Đổi ảnh khác
                  </Button>
                </Upload>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={handleRemoveImage}>
                  Xoá ảnh
                </Button>
              </div>
            </div>
          ) : (
            <Upload beforeUpload={handleFileChange} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />}>Chọn biểu tượng từ máy</Button>
            </Upload>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}

LevelModal.displayName = "LevelModal"

export default LevelModal
